import { Component, OnDestroy, OnInit } from '@angular/core'
import { MessageService } from 'primeng/api'
import { Subject, takeUntil } from 'rxjs'

import { CobranzaMonthlySummary, CollectionProgress } from '../../data/interfaces/monthly-summary.interface'
import { CobranzaShellService } from '../../data/services/view/cobranza-shell.service'
import { ApiCobranzaService } from '../../data/services/api/api-cobranza.service'
import { entregarDescarga } from '../../data/utils/entregar-descarga.util'
import { ApiF098Service } from '../../data/services/api/api-f098.service'
import { CollectionSeries } from '../../data/interfaces/f098.interface'

/**
 * El avance de cobranza: cómo va el mes contra su meta, y cómo han ido los anteriores.
 *
 * ## Las dos lentes van con nombre propio y no se mezclan
 *
 * - **Exigible** — todo el dinero del mes contra lo que ya venció. Es lo que la plataforma sabe.
 * - **Facturada** — lo cobrado de lo facturado contra lo facturado. Es como mide Contabilidad.
 *
 * Dan números distintos y eso es correcto. En la hoja de Contabilidad están confundidas en una sola
 * cifra, y ese es justamente el defecto que esta pantalla corrige: cada una lleva su etiqueta y su
 * explicación, para que nadie las sume ni las promedie.
 *
 *! La lente exigible **puede pasar del 100 %** cuando entra cartera vieja, y no es un error: el
 *! numerador es todo el efectivo del mes y el denominador solo las letras vencidas de ese mes.
 *
 * ## La serie histórica dice de qué pie cojea
 *
 * Solo la cartera al cierre está congelada. Lo demás se reconstruye en cada consulta, y una edición
 * de letras o la anulación de un pago lo mueven hacia atrás. La tabla marca cada celda con su base
 * en vez de presentarlas todas con la misma cara.
 */
@Component({
  selector: 'app-avance',
  templateUrl: './avance.component.html',
  styleUrls: ['./avance.component.scss'],
})
export class AvanceComponent implements OnInit, OnDestroy {
  summary: CobranzaMonthlySummary | null = null
  serie: CollectionSeries | null = null
  loading = false
  loadingSerie = false
  /** Qué hoja se está bajando, para que su botón muestre la espera. */
  exportando: 'avance' | 'dashboard' | null = null

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiF098Service,
    private readonly apiCobranza: ApiCobranzaService,
    private readonly shell: CobranzaShellService,
    private readonly toast: MessageService,
  ) {}

  /** AVANCE COBRANZA o DASHBOARD COBRANZA con la estructura de Contabilidad. */
  exportar(hoja: 'avance' | 'dashboard' = 'avance'): void {
    this.exportando = hoja
    this.api.hoja(hoja, this.month).subscribe((d) => {
      this.exportando = null
      entregarDescarga(d, `${hoja === 'avance' ? 'AVANCE COBRANZA' : 'DASHBOARD COBRANZA'} ${this.month}.xlsx`, this.toast, hoja === 'avance' ? 'Avance' : 'Dashboard')
    })
  }

  ngOnInit(): void {
    this.shell.month$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar())
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar())
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get month(): string {
    const d = this.shell.month || new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  cargar(): void {
    this.loading = true
    this.loadingSerie = true
    const mes = this.month

    this.apiCobranza.monthlySummary(mes).subscribe((r) => {
      this.loading = false
      this.summary = r.data
      if (r.error) this.toast.add({ severity: 'error', summary: 'Avance', detail: r.msg })
    })

    //* Doce meses hacia atrás desde el mes elegido: un año es lo que se compara en una junta.
    this.api.series(this.restar(mes, 11), mes).subscribe((r) => {
      this.loadingSerie = false
      this.serie = r.data
    })
  }

  /** La lente exigible del mes en curso. */
  get exigible(): CollectionProgress | null {
    return this.summary?.progress?.exigible ?? null
  }

  get facturada(): CollectionProgress | null {
    return this.summary?.progress?.invoiced ?? null
  }

  /** El color del semáforo. Lo resuelve el servidor; aquí solo se traduce a clase. */
  clase(p: CollectionProgress | null): string {
    if (!p) return 'is-na'
    return { ON_TARGET: 'is-ok', NEAR: 'is-near', ALERT: 'is-alert', NA: 'is-na' }[p.status] || 'is-na'
  }

  /**
   * El porcentaje como texto. `null` es «no aplica», no «0 %».
   *
   *! Por encima del 200 % se dice **en veces** («45×») y no en porcentaje. La lente exigible puede
   *! dispararse cuando entra cartera vieja o un enganche contra un mes con poco vencido — se vio en
   *! pantalla un **4506.3 %**, que es correcto y no comunica nada: nadie lee cuatro cifras de
   *! porcentaje como una proporción. «45× lo vencido» sí se lee.
   */
  pct(rate: number | null | undefined): string {
    if (rate == null) return 'n/a'
    if (rate > 2) return `${Math.round(rate)}×`
    return `${Math.round(rate * 1000) / 10}%`
  }

  /**
   * La brecha con su signo, para poder leerla de un vistazo.
   *
   *! Cuando la tasa se sale de escala —y se enseña en veces— la brecha en puntos deja de significar
   *! algo: «+4416.3 pts vs meta» es ruido al lado de «45×». Se calla, y el renglón de importes de
   *! abajo ya dice lo que pasó.
   */
  brecha(p: CollectionProgress | null): string {
    if (!p || p.gap == null || p.rate == null) return ''
    if (p.rate > 2) return 'muy por encima de la meta'
    const v = Math.round(p.gap * 1000) / 10
    //* La meta se dice con su número: «-24.1 pts vs meta» obliga a saberse de memoria contra qué se
    //* está midiendo, y es la cifra con la que alguien va a discutir el resultado.
    return `${v > 0 ? '+' : ''}${v} pts vs meta ${Math.round(p.target * 100)} %`
  }

  private restar(month: string, n: number): string {
    const [y, m] = month.split('-').map(Number)
    const t = y * 12 + (m - 1) - n
    return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`
  }
}
